import { useMemo } from 'react';
import type { Deployment, DriftStatusType } from '../../../types/backend';
import { DriftStatus } from '../../../types/backend';
import type { ServiceComponentSummary } from '../services/catalogService';

export interface DependencyInfo {
  productName: string;
  met: boolean;
  status: DriftStatusType | 'missing';
}

export function useServiceComponentDependencies(
  deployments: Deployment[],
  products: ServiceComponentSummary[],
): Record<string, DependencyInfo[]> {
  return useMemo(() => {
    if (deployments.length === 0 || products.length === 0) return {};

    const productByName = new Map<string, ServiceComponentSummary>();
    for (const p of products) {
      productByName.set(p.name, p);
    }

    const result: Record<string, DependencyInfo[]> = {};

    for (const deployment of deployments) {
      const product = productByName.get(deployment.product_name ?? '') ?? productByName.get(deployment.product_id);
      if (!product || product.depends_on.length === 0) continue;

      const deps: DependencyInfo[] = [];

      for (const depName of product.depends_on) {
        const depDeployment = deployments.find(
          (d) =>
            (d.product_name === depName || d.product_id === depName) &&
            d.account_id === deployment.account_id &&
            d.region === deployment.region,
        );

        if (!depDeployment) {
          deps.push({ productName: depName, met: false, status: 'missing' });
        } else {
          const met = depDeployment.drift_status === DriftStatus.IN_SYNC;
          deps.push({ productName: depName, met, status: depDeployment.drift_status });
        }
      }

      if (deps.length > 0) {
        result[deployment.id] = deps;
      }
    }

    return result;
  }, [deployments, products]);
}
